import {
  DocumentType,
  IDENTITY_MATCH_LABELS,
  MANAGER_ROLES,
  type IdentityConflictDetails,
  type LeadDetail,
  type UpdateContactPayload,
} from '@performance-crm/shared';
import { AlertTriangle, GitMerge } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { ContactDistrictField } from '@/components/leads/ContactDistrictField';
import { LeadSummaryLine, MergeContactsDialog } from '@/components/leads/MergeContactsDialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ui/toast-context';
import { ApiRequestError } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { useUpdateContact } from '@/lib/queries';

const formOf = (c: LeadDetail['contact']) => ({
  firstName: c.firstName,
  lastNamePaternal: c.lastNamePaternal ?? '',
  lastNameMaternal: c.lastNameMaternal ?? '',
  phone: c.phone ?? '',
  email: c.email ?? '',
  documentType: (c.documentType ?? '') as '' | DocumentType,
  documentNumber: c.documentNumber ?? '',
  district: c.district ?? '',
  province: c.province ?? '',
  department: c.department ?? '',
  occupation: c.occupation ?? '',
  birthDate: c.birthDate ?? '',
});

/**
 * Datos del cliente: nombres, celular, correo y documento (con revalidación de identidad en la API) y los datos que
 * alimentan el perfil del comprador. Si el dato nuevo ya es de otra persona, se ofrece fusionar en vez de guardar.
 */
export function ContactEditDialog({
  lead,
  open,
  onOpenChange,
}: {
  lead: LeadDetail;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const update = useUpdateContact(lead.id);
  const toast = useToast();
  const { user } = useAuth();
  const canMerge = !!user?.memberships.some(
    (m) => m.companyId === lead.companyId && MANAGER_ROLES.includes(m.role),
  );
  const c = lead.contact;
  const [f, setF] = useState(() => formOf(c));
  const [conflict, setConflict] = useState<IdentityConflictDetails | null>(null);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setF(formOf(c));
      setConflict(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- se rellena al abrir con los datos vigentes del contacto
  }, [open, c.id]);
  const set = (patch: Partial<typeof f>) => setF((p) => ({ ...p, ...patch }));

  const payload = (): UpdateContactPayload => ({
    firstName: f.firstName.trim() || undefined,
    lastNamePaternal: f.lastNamePaternal || null,
    lastNameMaternal: f.lastNameMaternal || null,
    phone: f.phone || null,
    email: f.email || null,
    documentType: f.documentType || null,
    documentNumber: f.documentNumber || null,
    district: f.district || null,
    province: f.province || null,
    department: f.department || null,
    occupation: f.occupation || null,
    birthDate: f.birthDate ? new Date(`${f.birthDate}T12:00:00`) : null,
  });

  const save = async () => {
    setConflict(null);
    setError(null);
    try {
      await update.mutateAsync({ contactId: c.id, input: payload() });
      toast.success('Datos del cliente guardados');
      onOpenChange(false);
    } catch (err) {
      const details =
        err instanceof ApiRequestError
          ? (err.body.details as Partial<IdentityConflictDetails> | undefined)
          : undefined;
      if (
        err instanceof ApiRequestError &&
        err.status === 409 &&
        details?.code === 'IDENTITY_CONFLICT'
      ) {
        setConflict(details as IdentityConflictDetails);
        return;
      }
      setError((err as Error).message);
    }
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    void save();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>Datos del cliente</DialogTitle>
            <DialogDescription>
              Corrige nombres, celular, correo y documento (si el dato ya es de otra persona, te lo
              avisamos antes de guardar) y completa dónde vive, a qué se dedica y cuándo nació para
              el perfil del comprador. Todo cambio queda en el historial.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="ce-first">Nombres</Label>
              <Input
                id="ce-first"
                value={f.firstName}
                onChange={(e) => set({ firstName: e.target.value })}
                maxLength={120}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ce-last-p">Apellido paterno</Label>
              <Input
                id="ce-last-p"
                value={f.lastNamePaternal}
                onChange={(e) => set({ lastNamePaternal: e.target.value })}
                maxLength={120}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ce-last-m">Apellido materno</Label>
              <Input
                id="ce-last-m"
                value={f.lastNameMaternal}
                onChange={(e) => set({ lastNameMaternal: e.target.value })}
                maxLength={120}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ce-phone">Celular</Label>
              <Input
                id="ce-phone"
                value={f.phone}
                onChange={(e) => set({ phone: e.target.value })}
                maxLength={40}
                inputMode="tel"
                placeholder="987 654 321"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ce-email">Correo</Label>
              <Input
                id="ce-email"
                type="email"
                value={f.email}
                onChange={(e) => set({ email: e.target.value })}
                maxLength={160}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ce-doc-type">Tipo de documento</Label>
              <Select
                id="ce-doc-type"
                value={f.documentType}
                onChange={(e) => set({ documentType: e.target.value as '' | DocumentType })}
              >
                <option value="">—</option>
                {Object.values(DocumentType).map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ce-doc">N.º de documento</Label>
              <Input
                id="ce-doc"
                value={f.documentNumber}
                onChange={(e) => set({ documentNumber: e.target.value })}
                maxLength={30}
              />
            </div>
            <div className="sm:col-span-2">
              <ContactDistrictField
                id="ce-district"
                value={f.district}
                onChange={(v) => set({ district: v })}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ce-province">Provincia</Label>
              <Input
                id="ce-province"
                value={f.province}
                onChange={(e) => set({ province: e.target.value })}
                maxLength={80}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ce-department">Departamento</Label>
              <Input
                id="ce-department"
                value={f.department}
                onChange={(e) => set({ department: e.target.value })}
                maxLength={80}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ce-occupation">Ocupación</Label>
              <Input
                id="ce-occupation"
                value={f.occupation}
                onChange={(e) => set({ occupation: e.target.value })}
                maxLength={120}
                placeholder="p. ej. Ingeniera"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ce-birth">Fecha de nacimiento</Label>
              <Input
                id="ce-birth"
                type="date"
                value={f.birthDate}
                onChange={(e) => set({ birthDate: e.target.value })}
              />
            </div>
          </div>
          {conflict && (
            <div
              className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:bg-amber-950/30"
              data-testid="contact-identity-conflict"
            >
              <p className="flex items-start gap-2 font-medium">
                <AlertTriangle className="text-warning mt-0.5 h-4 w-4 shrink-0" />
                Ese {IDENTITY_MATCH_LABELS[conflict.matchedOn]} ({conflict.value}) ya pertenece a{' '}
                {conflict.contact.name}.
              </p>
              {conflict.contact.leads.length > 0 && (
                <ul className="space-y-0.5 pl-6 text-xs">
                  {conflict.contact.leads.map((l) => (
                    <li key={l.id}>
                      <LeadSummaryLine lead={l} />
                    </li>
                  ))}
                </ul>
              )}
              {canMerge ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span>Si es la misma persona, fusiona a las dos en este cliente:</span>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setMerging(true)}
                    data-testid="contact-conflict-merge"
                  >
                    <GitMerge className="h-4 w-4" /> Fusionar
                  </Button>
                </div>
              ) : (
                <p>
                  No se guardó. Si es la misma persona, pide a tu coordinador que fusione a las dos
                  desde Ingesta → Duplicados.
                </p>
              )}
            </div>
          )}
          {error && (
            <p className="text-danger text-sm" data-testid="contact-edit-error">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={update.isPending} data-testid="contact-edit-save">
              Guardar
            </Button>
          </DialogFooter>
        </form>
        {conflict && (
          <MergeContactsDialog
            open={merging}
            targetContactId={c.id}
            sourceContactIds={[conflict.contact.id]}
            onOpenChange={setMerging}
            // Tras la fusión el dato ya es de esta persona: se guarda la corrección que estaba pendiente.
            onMerged={() => void save()}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
